import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import {  useState } from "react";
import { addNotification } from "@/store";

export const Route = createFileRoute("/_public/contact")({
  component: ContactPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: "",
    type: "enquiry",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        service: "",
        message: "",
        type: "enquiry",
      });
      addNotification(
        "success",
        "Your message has been sent! We'll respond within 2 business hours.",
      );
    }, 1500);
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Rosewood Tax Consultancy Nairobi Kenya</title>
        <meta
          name="description"
          content="Contact Rosewood Tax Consultancy in Nairobi, Kenya. Book a free consultation, send an enquiry, or visit our Upper Hill office."
        />
      </Helmet>

      <section className="bg-cream pt-16 pb-14">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className=" text-5xl md:text-6xl font-semibold mb-4">
              Get in Touch
            </h1>
            <p className="text-lg text-neutral-800 max-w-xl">
              Whether you need urgent tax help or want to explore how we can
              support your business — we're here.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="space-y-6">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="border rounded-xl"
              >
                {[
                  {
                    icon: Phone,
                    title: "Phone",
                    lines: ["+254 703 173405"],
                    href: "tel:+254703173405",
                  },
                  {
                    icon: Mail,
                    title: "Email",
                    lines: ["info@rosewoodconsulting.co.ke"],
                    href: "mailto:info@rosewoodconsulting.co.ke",
                  },
                  {
                    icon: MapPin,
                    title: "Office",
                    lines: ["Upper Hill, Nairobi", "Kenya"],
                    href: "#map",
                  },
                  {
                    icon: Clock,
                    title: "Business Hours",
                    lines: [
                      "Mon – Fri: 8:00am – 5:30pm",
                      "Sat: 9:00am – 1:00pm (by appointment)",
                    ],
                    href: null,
                  },
                ].map(({ icon: Icon, title, lines, href }) => (
                  <div key={title} className="flex gap-4 p-5 rounded-xl">
                    <div className="w-11 h-11 bg-burgundy-light rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon size={18} className="text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gold uppercase tracking-wide mb-1">
                        {title}
                      </p>
                      {lines.map((line) =>
                        href ? (
                          <a
                            key={line}
                            href={href}
                            className="block font-medium hover:text-burgundy transition-colors"
                          >
                            {line}
                          </a>
                        ) : (
                          <p key={line}>
                            {line}
                          </p>
                        ),
                      )}
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>

            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 }}
              >
                <div className="flex gap-2 mb-8">
                  {[
                    { value: "enquiry", label: "Send Enquiry" },
                    {
                      value: "consultation",
                      label: "Book Consultation",
                    },
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() =>
                        setFormData((p) => ({ ...p, type: value }))
                      }
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-xl  font-semibold transition-all ${
                        formData.type === value
                          ? "bg-burgundy text-white shadow-xs"
                          : "bg-cream text-neutral-800 border border-[#E8E0D0] hover:border-[#6B1E2A]/30"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold mb-1.5">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your full name"
                        className="w-full px-4 py-3 border border-[#E8E0D0] rounded-xl  focus:outline-none focus:border-[#6B1E2A]/40 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1.5">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="you@company.com"
                        className="w-full px-4 py-3 border border-[#E8E0D0] rounded-xl  focus:outline-none focus:border-[#6B1E2A]/40 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold mb-1.5">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+254 703 173405"
                        className="w-full px-4 py-3 border border-[#E8E0D0] rounded-xl  focus:outline-none focus:border-[#6B1E2A]/40 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1.5">
                        Company/Organisation
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your company name"
                        className="w-full px-4 py-3 border border-[#E8E0D0] rounded-xl  focus:outline-none focus:border-[#6B1E2A]/40 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-1.5">
                      Service Required <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-[#E8E0D0] rounded-xl  focus:outline-none focus:border-[#6B1E2A]/40 transition-colors appearance-none bg-white"
                    >
                      <option value="">Select a service...</option>
                      <option>KRA Registration</option>
                      <option>Tax Returns Filing</option>
                      <option>eTIMS Compliance</option>
                      <option>Bookkeeping & Payroll</option>
                      <option>Tax Advisory</option>
                      <option>KRA Audit Defence</option>
                      <option>International Tax Services</option>
                      <option>Tax Training</option>
                      <option>General Enquiry</option>
                    </select>
                  </div>

                  {formData.type === "consultation" && (
                    <div className="grid grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold mb-1.5">
                          Preferred Date
                        </label>
                        <input
                          type="date"
                          name="preferredDate"
                          className="w-full px-4 py-3 border border-[#E8E0D0] rounded-xl  focus:outline-none focus:border-[#6B1E2A]/40 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-1.5">
                          Preferred Time
                        </label>
                        <select className="w-full px-4 py-3 border border-[#E8E0D0] rounded-xl  focus:outline-none focus:border-[#6B1E2A]/40 transition-colors bg-white">
                          <option>Morning (8am – 12pm)</option>
                          <option>Afternoon (12pm – 3pm)</option>
                          <option>Late Afternoon (3pm – 5:30pm)</option>
                        </select>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold mb-1.5">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder={
                        formData.type === "consultation"
                          ? "Briefly describe your tax situation and what you hope to discuss..."
                          : "Tell us about your tax needs or questions..."
                      }
                      rows={5}
                      className="w-full px-4 py-3 border border-[#E8E0D0] rounded-xl  focus:outline-none focus:border-[#6B1E2A]/40 transition-colors resize-none"
                    />
                  </div>

                  <motion.button
                    type="submit"
                    disabled={submitting}
                    whileTap={{ scale: 0.97 }}
                    className="w-full py-3.5 bg-burgundy text-white font-semibold rounded-xl hover:bg-[#4A1219] transition-colors shadow-xs shadow-[#6B1E2A]/20 disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <span className="flex items-center gap-2">
                        <svg
                          className="animate-spin w-4 h-4"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      <>
                        <Send size={16} />
                        {formData.type === "consultation"
                          ? "Request Consultation"
                          : "Send Enquiry"}
                      </>
                    )}
                  </motion.button>

                  <p className="text-center text-sm text-neutral-800">
                    We respond within 2 business hours. All information is kept
                    strictly confidential.
                  </p>
                </form>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section id="map" className="bg-white py-0">
        <div className="bg-burgundy/8 h-72 flex items-center justify-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <pattern
                id="mapgrid"
                width="50"
                height="50"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 50 0 L 0 0 0 50"
                  fill="none"
                  stroke="#6B1E2A"
                  strokeWidth="0.5"
                />
              </pattern>
              <rect width="100%" height="100%" fill="url(#mapgrid)" />
            </svg>
          </div>
          <div className="text-center relative z-10">
            <div className="w-16 h-16 bg-burgundy rounded-full flex items-center justify-center mx-auto mb-4 shadow-xs">
              <MapPin size={28} className="text-white" />
            </div>
            <h3 className=" text-xl font-semibold mb-1">
              Find Us in Upper Hill
            </h3>
            <p className=" text-neutral-800">
              Upper Hill, Nairobi, Kenya
            </p>
            <a
              href="https://maps.google.com"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-burgundy text-white  font-semibold rounded-xl hover:bg-[#4A1219] transition-colors"
            >
              Open in Google Maps
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
